import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Users, Phone, Plus, Search, MapPin, Briefcase, ChevronDown, ChevronUp, 
  User, Lock, Video, MessageCircle, MessageSquare, Edit, UserPlus, Loader2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import PreAssignedContactsPanel from '../components/PreAssignedContactsPanel';
import { usePageHeader } from '../context/PageHeaderContext';

const ContactsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contactsTab, setContactsTab] = useState('internal');
  const [contacts, setContacts] = useState({ internal: [], external: [] });
  const [onboardedUsers, setOnboardedUsers] = useState([]);
  const [showAddContactDialog, setShowAddContactDialog] = useState(false);
  const [addContactTab, setAddContactTab] = useState('internal');
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [newExternalContact, setNewExternalContact] = useState({
    first_name: '', last_name: '', phone: '', email: '', company: '', title: '', profile_picture: '', notes: ''
  });
  
  // Edit external contact state
  const [showEditContactDialog, setShowEditContactDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editContactForm, setEditContactForm] = useState({
    first_name: '', last_name: '', phone: '', email: '', company: '', title: '', profile_picture: '', notes: ''
  });

  // Unassigned contacts state
  const [unassignedContacts, setUnassignedContacts] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);

  const isOnboarded = user?.is_onboarded || user?.role === 'admin';

  // Mobile header — title "Contacts" + back arrow to /calls,
  // Master Contacts (admins only) and "+" Add icons before the bell.
  const isAdmin = user?.role === 'admin';
  usePageHeader({
    title: 'Contacts',
    back: '/calls',
    rightSlot: (
      <>
        {isAdmin && (
          <button
            onClick={() => navigate('/admin/master-contacts')}
            className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
            aria-label="Master contacts"
            data-testid="header-master-contacts-btn"
          >
            <Users className="w-5 h-5" />
          </button>
        )}
        <button
          onClick={() => setShowAddContactDialog(true)}
          className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
          aria-label="Add contact"
          data-testid="header-add-contact-btn"
        >
          <Plus className="w-5 h-5" />
        </button>
      </>
    ),
  }, [isAdmin]);

  // Deep-link: ?contact=<id> from a notification expands that contact in the External tab
  useEffect(() => {
    const cid = searchParams.get('contact');
    if (!cid) return;
    setContactsTab('external');
    setExpandedContactId(cid);
  }, [searchParams]);

  useEffect(() => {
    if (isOnboarded) {
      fetchContacts();
      fetchOnboardedUsers();
    } else {
      setLoading(false);
    }
  }, [isOnboarded]);

  useEffect(() => {
    if (isOnboarded && contactsTab === 'unassigned') {
      fetchUnassignedContacts();
    }
  }, [contactsTab, isOnboarded]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/contacts');
      setContacts(res.data);
    } catch (error) {
      setContacts({ internal: [], external: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnassignedContacts = async () => {
    setUnassignedLoading(true);
    try {
      const res = await api.get('/twilio/contacts/unassigned');
      setUnassignedContacts(res.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch unassigned contacts:', error);
      setUnassignedContacts([]);
    } finally {
      setUnassignedLoading(false);
    }
  };

  const fetchOnboardedUsers = async () => {
    try {
      const res = await api.get('/users/all');
      setOnboardedUsers(res.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const getInitials = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    }
    if (u?.full_name) {
      return u.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  const getFullName = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name} ${u.last_name}`;
    }
    return u?.full_name || 'Unknown User';
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-500/10 text-rose-600 border-rose-500/20';
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 border-slate-500/20';
    }
  };

  const handleAddInternalContact = async (u) => {
    try {
      await api.post('/contacts/internal', { user_id: u.id });
      // success is silent — the contact appears in the list immediately
      fetchContacts();
      setShowAddContactDialog(false);
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleAddExternalContact = async () => {
    if (!newExternalContact.first_name || !newExternalContact.last_name || !newExternalContact.phone) {
      toast.error('First name, last name, and phone are required');
      return;
    }
    try {
      // Combine first and last name for storage
      const contactData = {
        ...newExternalContact,
        name: `${newExternalContact.first_name} ${newExternalContact.last_name}`.trim()
      };
      await api.post('/contacts/external', contactData);
      // silent success
      setNewExternalContact({ first_name: '', last_name: '', phone: '', email: '', company: '', notes: '' });
      fetchContacts();
      setShowAddContactDialog(false);
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleOpenEditContact = (contact) => {
    setEditingContact(contact);
    // Split name into first and last
    const nameParts = (contact.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    setEditContactForm({
      first_name: firstName,
      last_name: lastName,
      phone: contact.phone || '',
      email: contact.email || '',
      company: contact.company || '',
      title: contact.title || '',
      profile_picture: contact.profile_picture || '',
      notes: contact.notes || ''
    });
    setShowEditContactDialog(true);
  };

  const handleEditExternalContact = async () => {
    if (!editContactForm.first_name || !editContactForm.last_name || !editContactForm.phone) {
      toast.error('First name, last name, and phone are required');
      return;
    }
    try {
      const contactData = {
        ...editContactForm,
        name: `${editContactForm.first_name} ${editContactForm.last_name}`.trim()
      };
      await api.put(`/contacts/external/${editingContact.id}`, contactData);
      // silent success
      setShowEditContactDialog(false);
      setEditingContact(null);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const toggleContactExpand = (contactId) => {
    setExpandedContactId(expandedContactId === contactId ? null : contactId);
  };

  const filteredContactUsers = onboardedUsers.filter(u =>
    getFullName(u).toLowerCase().includes(contactSearchQuery.toLowerCase())
  );

  // Non-onboarded blocker
  if (!isOnboarded) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="contacts-page">
        <div className="max-w-2xl mx-auto pt-4 relative">
          <div className="absolute inset-0 backdrop-blur-md bg-white/50 dark:bg-black/50 rounded-xl z-10 flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Complete Onboarding</h2>
            <p className="text-muted-foreground text-center max-w-sm mb-4">
              You need to complete your onboarding process before accessing contacts.
            </p>
            <Button onClick={() => navigate('/')}>Return to Onboarding</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24 space-y-4" data-testid="contacts-page">
      {/* Pre-assigned (pending Accept/Decline) */}
      <PreAssignedContactsPanel />

      {/* Toggle — Unassigned has its own dedicated page (button on /calls);
          Contacts only shows Internal vs External here. */}
      <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
        <CardContent className="p-2">
          <div className="flex rounded-xl bg-[#f5f5f5] dark:bg-[#0d0d0d] p-1">
            {['internal', 'external'].map((tab) => (
              <button
                key={tab}
                onClick={() => setContactsTab(tab)}
                className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                  contactsTab === tab
                    ? 'bg-white dark:bg-[#1a1a1a] text-[#1c1a1c] dark:text-white shadow-sm'
                    : 'text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'internal' ? 'Internal' : 'External'}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Contacts List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
          <span className="text-slate-500 text-sm">Loading contacts...</span>
        </div>
      ) : (
        <>
          {contactsTab === 'internal' ? (
            <>
              <div className="mb-3">
                <h2 className="text-base font-semibold">Internal Contacts</h2>
                <p className="text-xs text-muted-foreground">{contacts.internal?.length || 0} contacts</p>
              </div>
              {contacts.internal?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No internal contacts yet</p>
                  <p className="text-xs mt-1">Add onboarded users to your contacts</p>
                </div>
              ) : (
                <div className="space-y-1.5 pb-20">
                  {contacts.internal?.map((contact) => {
                    const isExpanded = expandedContactId === contact.id;
                    return (
                      <div
                        key={contact.id}
                        className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden transition-all"
                        data-testid={`contact-${contact.id}`}
                      >
                        {/* Collapsed View */}
                        <div
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                          onClick={() => toggleContactExpand(contact.id)}
                        >
                          <Avatar className="h-10 w-10 border-2 border-white dark:border-[#1a1a1a] shadow-sm">
                            <AvatarImage src={contact.profile_picture} />
                            <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(contact.role)} text-white text-sm`}>
                              {getInitials(contact)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{getFullName(contact)}</p>
                            <div className="flex items-center gap-2">
                              <Badge className={`text-[10px] capitalize px-1.5 py-0 border ${getRoleBadgeStyle(contact.role)}`}>
                                {contact.role}
                              </Badge>
                              {contact.state && (
                                <span className="text-xs text-muted-foreground">{contact.state}</span>
                              )}
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        
                        {/* Expanded View */}
                        {isExpanded && (
                          <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800">
                            <div className="space-y-3 mt-2">
                              {/* Contact Info - Outward facing profile info */}
                              <div className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                                <Avatar className="h-12 w-12">
                                  <AvatarImage src={contact.profile_picture} />
                                  <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(contact.role)} text-white`}>
                                    {getInitials(contact)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">{getFullName(contact)}</p>
                                  <p className="text-xs text-muted-foreground capitalize">{contact.role}</p>
                                </div>
                              </div>

                              {/* Public Info */}
                              <div className="space-y-1">
                                {contact.state && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span>{contact.state}</span>
                                  </div>
                                )}
                                {contact.industry && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span>{contact.industry}</span>
                                    {contact.industry_domain && (
                                      <span className="text-muted-foreground">• {contact.industry_domain}</span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Notes */}
                              {contact.notes && (
                                <div className="p-2 bg-muted/30 rounded-lg">
                                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                                  <p className="text-sm">{contact.notes}</p>
                                </div>
                              )}

                              {/* Action Buttons - Internal: Call, Video Call, Chat */}
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-green-600 hover:bg-green-700"
                                  onClick={() => navigate(`/calls?call=${contact.id}&mode=voice`)}
                                  data-testid={`contact-voice-call-${contact.id}`}
                                >
                                  <Phone className="w-3.5 h-3.5 mr-1.5" />
                                  Call
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                                  onClick={() => navigate(`/calls?call=${contact.id}&mode=video`)}
                                  data-testid={`contact-video-call-${contact.id}`}
                                >
                                  <Video className="w-3.5 h-3.5 mr-1.5" />
                                  Video Call
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-[#586D52] hover:bg-[#4a5d46]"
                                  onClick={() => navigate('/chat?tab=internal')}
                                >
                                  <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                                  Chat
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-3">
                <h2 className="text-base font-semibold">External Contacts</h2>
                <p className="text-xs text-muted-foreground">{contacts.external?.length || 0} contacts</p>
              </div>
              {contacts.external?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Phone className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No external contacts yet</p>
                  <p className="text-xs mt-1">Add external phone contacts</p>
                </div>
              ) : (
                <div className="space-y-1.5 pb-20">
                  {contacts.external?.map((contact) => {
                    const isExpanded = expandedContactId === contact.id;
                    return (
                      <div
                        key={contact.id}
                        className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden transition-all"
                        data-testid={`contact-${contact.id}`}
                      >
                        {/* Collapsed View */}
                        <div
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                          onClick={() => toggleContactExpand(contact.id)}
                        >
                          <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium text-sm truncate">{contact.name}</p>
                              {contact.sms_consent && (
                                <span
                                  title="Explicit SMS opt-in on file"
                                  className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 rounded-full px-1.5 py-[1px] shrink-0"
                                  data-testid={`contact-sms-consent-badge-${contact.id}`}
                                >
                                  <CheckCircle2 className="w-2.5 h-2.5" /> SMS
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{contact.phone}</p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        
                        {/* Expanded View - Compact */}
                        {isExpanded && (
                          <div className="px-3 pb-2 pt-1 border-t border-gray-100 dark:border-gray-800">
                            {/* Source label (e.g., business card) */}
                            {contact.source === 'business_card' && (
                              <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-purple-600 bg-purple-100 dark:bg-purple-900/30 rounded-full px-2 py-0.5 mt-1 mr-1 break-words">
                                From business card submission
                              </span>
                            )}
                            {/* SMS opt-in badge (full label in expanded view) */}
                            {contact.sms_consent && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 rounded-full px-2 py-0.5 mt-1 break-words"
                                data-testid={`contact-sms-consent-detail-${contact.id}`}
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                SMS opt-in
                                {contact.sms_consent_source === 'business_card_waitlist' && ' · Waitlist'}
                                {contact.sms_consent_source === 'business_card_lead' && ' · Card'}
                              </span>
                            )}

                            {/* Contact Details - Compact inline */}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1 break-words">
                              {contact.email && (
                                <span>✉ {contact.email}</span>
                              )}
                              {contact.company && (
                                <span>🏢 {contact.company}</span>
                              )}
                            </div>

                            {/* Submitter's note (auto-attached on card submission) */}
                            {contact.submitter_note && (
                              <div className="mt-1 rounded-md bg-purple-50 dark:bg-purple-950/40 px-2 py-1.5 break-words">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-purple-600">Note from submitter</p>
                                <p className="text-xs text-purple-900 dark:text-purple-200 italic break-words">"{contact.submitter_note}"</p>
                              </div>
                            )}

                            {/* User-editable Notes */}
                            {contact.notes && (
                              <p className="text-xs text-muted-foreground mt-1 italic break-words">"{contact.notes}"</p>
                            )}

                            {/* Action Buttons - Compact row */}
                            <div className="flex gap-1.5 mt-2">
                              <Button 
                                size="sm"
                                className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => navigate('/calls')}
                              >
                                <Phone className="w-3 h-3 mr-1" />
                                Call
                              </Button>
                              <Button 
                                size="sm"
                                className="flex-1 h-7 text-xs bg-purple-600 hover:bg-purple-700"
                                onClick={() => navigate('/chat?tab=external')}
                              >
                                <MessageSquare className="w-3 h-3 mr-1" />
                                Text
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                className="h-7 px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEditContact(contact);
                                }}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {contactsTab === 'unassigned' && (
            <>
              <div className="mb-3">
                <h2 className="text-base font-semibold">Unassigned Contacts</h2>
                <p className="text-xs text-muted-foreground">{unassignedContacts.length} unassigned</p>
              </div>
              {unassignedLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-3" />
                  <span className="text-muted-foreground text-sm">Loading...</span>
                </div>
              ) : unassignedContacts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserPlus className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No unassigned contacts</p>
                  <p className="text-xs mt-1">Contacts from the state number will appear here</p>
                </div>
              ) : (
                <div className="space-y-1.5 pb-20">
                  {unassignedContacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
                      data-testid={`unassigned-${contact.id}`}
                    >
                      <div className="flex items-center gap-2 px-3 py-2">
                        <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                          <User className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{contact.name || contact.phone_number}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{contact.phone_number}</span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-orange-50 text-orange-600 border-orange-200">{contact.state}</Badge>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => navigate('/unassigned-callers')}
                          data-testid={`manage-unassigned-${contact.id}`}
                        >
                          <UserPlus className="w-3 h-3 mr-1" />
                          Manage
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Add Contact Dialog */}
      <Dialog open={showAddContactDialog} onOpenChange={setShowAddContactDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Add Contact</DialogTitle>
          </DialogHeader>
          
          <div className="px-4 pb-2">
            <div className="flex rounded-xl bg-[#f5f5f5] dark:bg-[#0d0d0d] p-1 mb-3">
              {['internal', 'external'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setAddContactTab(tab)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all capitalize ${
                    addContactTab === tab
                      ? 'bg-white dark:bg-[#1a1a1a] shadow-sm'
                      : 'text-[#676767]'
                  }`}
                >
                  {tab === 'internal' ? 'Internal' : 'External'}
                </button>
              ))}
            </div>
          </div>
          
          <ScrollArea className="max-h-[50vh] px-4 pb-4">
            {addContactTab === 'internal' ? (
              <div className="space-y-2">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={contactSearchQuery}
                    onChange={(e) => setContactSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {filteredContactUsers.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                    onClick={() => handleAddInternalContact(u)}
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={u.profile_picture} />
                      <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(u.role)} text-white text-xs`}>
                        {getInitials(u)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{getFullName(u)}</p>
                      <p className="text-xs text-muted-foreground capitalize">{u.role}</p>
                    </div>
                    <Plus className="w-4 h-4 text-muted-foreground" />
                  </div>
                ))}
                {filteredContactUsers.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-4">No users found</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">First Name *</Label>
                    <Input
                      value={newExternalContact.first_name}
                      onChange={(e) => setNewExternalContact({...newExternalContact, first_name: e.target.value})}
                      className="mt-1"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Last Name *</Label>
                    <Input
                      value={newExternalContact.last_name}
                      onChange={(e) => setNewExternalContact({...newExternalContact, last_name: e.target.value})}
                      className="mt-1"
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Phone *</Label>
                  <Input
                    value={newExternalContact.phone}
                    onChange={(e) => setNewExternalContact({...newExternalContact, phone: e.target.value})}
                    className="mt-1"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={newExternalContact.email}
                    onChange={(e) => setNewExternalContact({...newExternalContact, email: e.target.value})}
                    className="mt-1"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label className="text-xs">Company</Label>
                  <Input
                    value={newExternalContact.company}
                    onChange={(e) => setNewExternalContact({...newExternalContact, company: e.target.value})}
                    className="mt-1"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={newExternalContact.title}
                    onChange={(e) => setNewExternalContact({...newExternalContact, title: e.target.value})}
                    className="mt-1"
                    placeholder="Role / job title"
                    data-testid="new-contact-title"
                  />
                </div>
                <div>
                  <Label className="text-xs">Profile Picture</Label>
                  <div className="mt-1 flex items-center gap-3">
                    {newExternalContact.profile_picture ? (
                      <img src={newExternalContact.profile_picture} alt="" className="w-12 h-12 rounded-full object-cover border" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#586D52]/15 flex items-center justify-center text-[#586D52] text-sm font-semibold">
                        {(newExternalContact.first_name?.[0] || '?').toUpperCase()}
                      </div>
                    )}
                    <label className="cursor-pointer text-xs text-[#586D52] hover:underline">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="new-contact-image"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const r = new FileReader();
                          r.onload = () => setNewExternalContact((s) => ({ ...s, profile_picture: r.result }));
                          r.readAsDataURL(f);
                        }}
                      />
                      Upload image
                    </label>
                    {newExternalContact.profile_picture && (
                      <button
                        type="button"
                        onClick={() => setNewExternalContact((s) => ({ ...s, profile_picture: '' }))}
                        className="text-xs text-red-600 hover:underline"
                      >Remove</button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <textarea
                    value={newExternalContact.notes}
                    onChange={(e) => setNewExternalContact({...newExternalContact, notes: e.target.value})}
                    className="mt-1 w-full min-h-[60px] p-2 rounded-lg border bg-background resize-none text-sm focus:outline-none focus:ring-2 focus:ring-[#586D52]/40 focus:border-[#586D52]"
                    placeholder="Additional notes..."
                  />
                </div>
                <Button 
                  className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
                  onClick={handleAddExternalContact}
                >
                  Add External Contact
                </Button>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Edit External Contact Dialog */}
      <Dialog open={showEditContactDialog} onOpenChange={setShowEditContactDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Edit Contact</DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] px-4 pb-4">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">First Name *</Label>
                  <Input
                    value={editContactForm.first_name}
                    onChange={(e) => setEditContactForm({...editContactForm, first_name: e.target.value})}
                    className="mt-1"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Last Name *</Label>
                  <Input
                    value={editContactForm.last_name}
                    onChange={(e) => setEditContactForm({...editContactForm, last_name: e.target.value})}
                    className="mt-1"
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input
                  value={editContactForm.phone}
                  onChange={(e) => setEditContactForm({...editContactForm, phone: e.target.value})}
                  className="mt-1"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  value={editContactForm.email}
                  onChange={(e) => setEditContactForm({...editContactForm, email: e.target.value})}
                  className="mt-1"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <Input
                  value={editContactForm.company}
                  onChange={(e) => setEditContactForm({...editContactForm, company: e.target.value})}
                  className="mt-1"
                  placeholder="Company name"
                />
              </div>
              <div>
                <Label className="text-xs">Title</Label>
                <Input
                  value={editContactForm.title}
                  onChange={(e) => setEditContactForm({...editContactForm, title: e.target.value})}
                  className="mt-1"
                  placeholder="Role / job title"
                  data-testid="edit-contact-title"
                />
              </div>
              <div>
                <Label className="text-xs">Profile Picture</Label>
                <div className="mt-1 flex items-center gap-3">
                  {editContactForm.profile_picture ? (
                    <img src={editContactForm.profile_picture} alt="" className="w-12 h-12 rounded-full object-cover border" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#586D52]/15 flex items-center justify-center text-[#586D52] text-sm font-semibold">
                      {(editContactForm.first_name?.[0] || '?').toUpperCase()}
                    </div>
                  )}
                  <label className="cursor-pointer text-xs text-[#586D52] hover:underline">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="edit-contact-image"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const r = new FileReader();
                        r.onload = () => setEditContactForm((s) => ({ ...s, profile_picture: r.result }));
                        r.readAsDataURL(f);
                      }}
                    />
                    Upload image
                  </label>
                  {editContactForm.profile_picture && (
                    <button
                      type="button"
                      onClick={() => setEditContactForm((s) => ({ ...s, profile_picture: '' }))}
                      className="text-xs text-red-600 hover:underline"
                    >Remove</button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <textarea
                  value={editContactForm.notes}
                  onChange={(e) => setEditContactForm({...editContactForm, notes: e.target.value})}
                  className="mt-1 w-full min-h-[60px] p-2 rounded-lg border bg-background resize-none text-sm focus:outline-none focus:border-[#586D52] focus:ring-1 focus:ring-inset focus:ring-[#586D52]/40"
                  placeholder="Additional notes..."
                />
              </div>
              <Button 
                className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
                onClick={handleEditExternalContact}
              >
                Save Changes
              </Button>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
